import { siteWallClock } from "@/lib/datetime/site-timezone";

export const UPLOAD_DIR_RULE_OPTIONS = [
  { value: "yyyy", label: "yyyy（2026）" },
  { value: "yyyymm", label: "yyyymm（202606）" },
  { value: "yyyymmdd", label: "yyyymmdd（20260609）" },
  { value: "yyyy/mmdd", label: "yyyy/mmdd（2026/0609）" },
  { value: "yyyy/mm/dd", label: "yyyy/mm/dd（2026/06/09）" },
  { value: "yyyy/mm", label: "yyyy/mm（2026/06）（推荐）" },
  { value: "none", label: "不分目录" },
] as const;

export const DEFAULT_UPLOAD_DIR_RULE = "yyyy/mm";

export const UPLOAD_NAME_RULE_OPTIONS = [
  { value: "mm_random", label: "月份+随机" },
  { value: "date_random", label: "日期+随机（推荐）" },
  { value: "hhmm_random", label: "时分+随机" },
  { value: "uuid_random", label: "全部随机" },
] as const;

export const DEFAULT_UPLOAD_NAME_RULE = "date_random";

export type UploadRelativeDirInput = {
  dirRule: string;
  date?: Date;
};

const DIR_RULE_PRESETS: Record<string, (parts: DateParts) => string> = {
  yyyy: (parts) => parts.year,
  yyyymmdd: (parts) => `${parts.year}${parts.month}${parts.day}`,
  yyyymm: (parts) => `${parts.year}${parts.month}`,
  "yyyy/mmdd": (parts) => `${parts.year}/${parts.month}${parts.day}`,
  "yyyy/mm/dd": (parts) => `${parts.year}/${parts.month}/${parts.day}`,
  "yyyy/mm": (parts) => `${parts.year}/${parts.month}`,
};

type DateParts = {
  year: string;
  month: string;
  day: string;
};

function getDateParts(date: Date): DateParts {
  const parts = siteWallClock(date);

  return {
    year: String(parts.year),
    month: String(parts.month).padStart(2, "0"),
    day: String(parts.day).padStart(2, "0"),
  };
}

function resolveDateSubdirFromTemplate(dirRule: string, parts: DateParts) {
  return dirRule
    .replace(/\{yyyy\}/gi, parts.year)
    .replace(/\{yy\}/gi, parts.year.slice(-2))
    .replace(/\{mm\}/gi, parts.month)
    .replace(/\{dd\}/gi, parts.day)
    .replace(/\/+/g, "/")
    .replace(/^\/|\/$/g, "");
}

function sanitizeDateSubdir(value: string) {
  const sanitized = value.replace(/\\/g, "/").replace(/\/+/g, "/").replace(/^\/|\/$/g, "");

  if (!sanitized || !/^[\w/]+$/.test(sanitized)) {
    return null;
  }

  return sanitized;
}

export function resolveDateSubdir(dirRule: string, date = new Date()) {
  const normalized = dirRule.trim();

  if (!normalized || normalized.toLowerCase() === "none") {
    return "";
  }

  const parts = getDateParts(date);
  const preset = DIR_RULE_PRESETS[normalized.toLowerCase()];

  if (preset) {
    return sanitizeDateSubdir(preset(parts)) ?? "";
  }

  const templated = resolveDateSubdirFromTemplate(normalized, parts);

  return sanitizeDateSubdir(templated) ?? DIR_RULE_PRESETS["yyyy"](parts);
}

export function resolveUploadRelativeDir(input: UploadRelativeDirInput) {
  const date = input.date ?? new Date();
  const segments = ["uploads"];
  const dateSubdir = resolveDateSubdir(input.dirRule, date);

  if (dateSubdir) {
    segments.push(...dateSubdir.split("/").filter(Boolean));
  }

  if (segments.length === 1) {
    segments.push(getDateParts(date).year);
  }

  return segments.join("/");
}

export function formatUploadDirPreview(input: UploadRelativeDirInput) {
  return `/${resolveUploadRelativeDir(input)}/`;
}
